import { forwardRef, type ElementType, type MouseEventHandler, type ReactNode } from 'react'
import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'

import { tokens } from '@configuration/theme'

export type ButtonVariant = 'primary' | 'bordered' | 'ghost' | 'paretos' | 'link'
export type ButtonSize = 'md' | 'lg'

// Heights: `md` (36px) for in-card form actions (settings), `lg` (44px) for
// page-level CTAs (login sign-in). Mirrors the two `.btn` definitions in the
// design's Login Page / Profile Settings stylesheets.
const SIZE_SX: Record<ButtonSize, SxProps<Theme>> = {
  md: { height: 36, padding: '0 16px', fontSize: 14 },
  lg: { height: 44, padding: '0 20px', fontSize: 15 },
}

const VARIANT_SX: Record<ButtonVariant, SxProps<Theme>> = {
  primary: {
    background: tokens.businessBlack,
    color: tokens.white,
    border: 0,
    '&:hover': { opacity: 0.6 },
    '&:active': { opacity: 1 },
    '&:disabled, &[aria-disabled="true"]': {
      background: tokens.businessBlack5Solid,
      color: 'rgba(0,0,0,0.2)',
      cursor: 'not-allowed',
      opacity: 1,
    },
  },
  bordered: {
    background: tokens.white,
    color: tokens.businessBlack,
    border: `1px solid ${tokens.borderDefault}`,
    '&:hover': { background: tokens.smokeySilver, borderColor: tokens.businessBlack60 },
    '&:disabled, &[aria-disabled="true"]': {
      background: tokens.smokeySilver,
      color: tokens.businessBlack40,
      borderColor: tokens.borderSubtle,
      cursor: 'not-allowed',
    },
  },
  ghost: {
    background: 'transparent',
    color: tokens.businessBlack,
    border: 0,
    '&:hover': { background: tokens.smokeySilver },
  },
  paretos: {
    background: tokens.paretosGradient,
    color: tokens.white,
    border: 0,
    '&:hover': { opacity: 0.6 },
  },
  // Text-sized, not form-control-sized — inline escape hatches (SsoSection), not CTAs.
  link: {
    background: 'transparent',
    color: tokens.versatileViolet,
    border: 0,
    padding: 0,
    height: 'auto',
    fontWeight: 400,
    fontSize: '0.875rem',
    lineHeight: 1.43,
    '&:hover': { textDecoration: 'underline' },
  },
}

export interface ButtonProps {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Stretch to the full width of the container. */
  block?: boolean
  /** Render as a different element — e.g. `'a'` or react-router's `Link`. */
  component?: ElementType
  type?: 'button' | 'submit' | 'reset'
  name?: string
  value?: string
  href?: string
  /** react-router `Link` target (used with `component={Link}`). */
  to?: string
  disabled?: boolean
  onClick?: MouseEventHandler<HTMLElement>
  target?: string
  rel?: string
  title?: string
  'aria-label'?: string
  dataTestId?: string
  className?: string
  sx?: SxProps<Theme>
  children?: ReactNode
}

// Presentational button matching the paretos design system. Polymorphic via
// `component` so the same look applies to <button> submits, anchors, and
// react-router <Link>s. No business logic — callers wire `type`/`name`/`value`
// /`onClick` (the OIDC + form-submit invariants live in the feature code).
// forwardRef so it can serve as a MUI Menu/Tooltip anchor.
const Button = forwardRef<HTMLElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'lg',
    block = false,
    component = 'button',
    sx,
    children,
    dataTestId = 'button',
    ...rest
  },
  ref,
) {
  return (
    <Box
      ref={ref}
      component={component}
      data-testid={dataTestId}
      sx={[
        {
          appearance: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontWeight: 500,
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          borderRadius: tokens.radiusPill,
          textDecoration: 'none',
          transition: 'background-color .3s, color .3s, opacity .3s, border-color .3s',
          width: block ? '100%' : 'auto',
          '&:focus-visible': { outline: `2px solid ${tokens.versatileViolet}`, outlineOffset: 2 },
        },
        SIZE_SX[size],
        VARIANT_SX[variant],
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {children}
    </Box>
  )
})

export default Button
